import { useState, useMemo, useCallback, memo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  SortingState,
} from "@tanstack/react-table";
import { DataTable } from "@/components/data-table/data-table";
import {
  CampaignCode,
  useCampaignCodes,
  useDeleteCampaignCode,
} from "@/lib/api/campaign-codes";
import { columns } from "./columns";
import { CreateCampaignCodeDialog } from "./create-campaign-code-dialog";
import { EditCampaignCodeDialog } from "./edit-campaign-code-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pencil, Users, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { useNavigate } from "@tanstack/react-router";
import { useAbility } from "@/lib/casl/ability-context";

interface ActionCellProps {
  code: CampaignCode;
  canViewUsers: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  onEdit: (code: CampaignCode) => void;
  onDelete: (code: CampaignCode) => void;
  onViewUsers: (id: string) => void;
}

const ActionCell = memo(function ActionCell({
  code,
  canViewUsers,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
  onViewUsers,
}: ActionCellProps) {
  return (
    <div className="flex items-center gap-1">
      {canViewUsers && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onViewUsers(code.id)}
              >
                <Users className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              View Users ({code.redeemed_count ?? 0})
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {canUpdate && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => onEdit(code)}>
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Edit</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {canDelete && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(code)}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Delete</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
});

export function CampaignCodesPage() {
  const navigate = useNavigate();
  const { data: campaignCodes, isLoading } = useCampaignCodes();
  const deleteMutation = useDeleteCampaignCode();
  const ability = useAbility();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [editingCode, setEditingCode] = useState<CampaignCode | null>(null);
  const [deletingCode, setDeletingCode] = useState<CampaignCode | null>(null);

  const canViewUsers = ability.can("read", "users");
  const canCreate = ability.can("create", "campaign-codes");
  const canUpdate = ability.can("update", "campaign-codes");
  const canDelete = ability.can("delete", "campaign-codes");

  const handleViewUsers = useCallback(
    (id: string) => {
      navigate({ to: "/campaign-codes/$codeId", params: { codeId: id } });
    },
    [navigate],
  );

  const columnsWithActions = useMemo(
    () => [
      ...columns,
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }: { row: { original: CampaignCode } }) => (
          <ActionCell
            code={row.original}
            canViewUsers={canViewUsers}
            canUpdate={canUpdate}
            canDelete={canDelete}
            onEdit={setEditingCode}
            onDelete={setDeletingCode}
            onViewUsers={handleViewUsers}
          />
        ),
      },
    ],
    [handleViewUsers, canUpdate, canDelete, canViewUsers],
  );

  const table = useReactTable({
    data: campaignCodes ?? [],
    columns: columnsWithActions,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      globalFilter,
    },
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold leading-7 text-black">
          Campaign Codes
        </h2>
        {canCreate && <CreateCampaignCodeDialog />}
      </div>

      <div className="mt-6">
        <div className="mb-4">
          <Input
            placeholder="Search codes..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm"
          />
        </div>
        <DataTable table={table} isPending={isLoading} disablePagination />
      </div>

      {canUpdate && (
        <EditCampaignCodeDialog
          campaignCode={editingCode}
          open={!!editingCode}
          onOpenChange={(open) => !open && setEditingCode(null)}
        />
      )}

      {canDelete && (
        <ConfirmActionDialog
          isOpen={!!deletingCode}
          onClose={() => setDeletingCode(null)}
          onConfirm={() => {
            if (deletingCode) {
              deleteMutation.mutate(deletingCode.id);
              setDeletingCode(null);
            }
          }}
          title="Delete Campaign Code"
          description={`Are you sure you want to delete the campaign code "${deletingCode?.code}"? This action cannot be undone.`}
          confirmText="Delete"
          loading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}
